import { useEffect, useMemo, useState } from 'react'
import { fxExpensesApi, fxSuppliersApi } from '../../services/api'
import ExportBar, { ExportColumn } from '../../components/ExportBar'
import TableActionButton from '../../components/TableActionButton'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'

type SupplierType = 'producto' | 'servicio'
type ServiceCategory = 'marketing' | 'logistica' | 'contabilidad' | 'otro'

// "Logística" is deliberately labeled "Logística / Distribución" everywhere
// on this page -- there is no separate "Distribución" bucket in the schema,
// so this makes explicit that distribution/shipping costs belong here.
const SERVICE_CATEGORY_LABELS: Record<ServiceCategory, string> = {
  marketing: 'Marketing',
  logistica: 'Logística / Distribución',
  contabilidad: 'Contabilidad',
  otro: 'Otro',
}

const SERVICE_CATEGORY_ORDER: ServiceCategory[] = ['marketing', 'logistica', 'contabilidad', 'otro']

const PAYMENT_METHOD_OPTIONS = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'tarjeta', label: 'Tarjeta' },
]

interface Expense {
  id: number
  category: string
  description: string
  amount: number
  expense_date: string
  payment_method: string | null
  supplier_id: number | null
  supplier_name: string | null
  notes: string | null
  is_active: boolean
  supplier_type: SupplierType | null
  service_category: ServiceCategory | null
}

interface Supplier {
  id: number
  name: string
  supplier_type: SupplierType
  service_category: ServiceCategory | null
  is_active: boolean
}

interface ServiceCostForm {
  supplier_id: string
  description: string
  amount: string
  expense_date: string
  payment_method: string
  notes: string
  // Not shown in the form -- 'servicios' for a brand-new row, or the row's
  // real existing category when editing, so saving an edit (even with no
  // changes) never silently reclassifies a pre-existing expense that might
  // have a different category (e.g. 'transporte') for a perfectly valid
  // reason unrelated to this page.
  category: string
}

const todayIso = () => new Date().toISOString().split('T')[0]

const emptyForm = (): ServiceCostForm => ({
  supplier_id: '',
  description: '',
  amount: '',
  expense_date: todayIso(),
  payment_method: '',
  notes: '',
  category: 'servicios',
})

const categoryBadgeLabel = (category: ServiceCategory | null) =>
  category ? SERVICE_CATEGORY_LABELS[category] : 'Sin categoría'

/** Focused view over the generic distribution_fx.expenses table (the same
 * one FxExpensesTab manages) scoped to expenses linked to a "servicio"-type
 * supplier -- marketing, logística/distribución, contabilidad, otro. A new
 * row created here is sent with category: "servicios" (the closest bucket
 * in the generic 6-value enum); the real classification for this page's
 * purposes comes from the supplier's service_category instead. Editing a
 * row preserves whatever category it already had -- it may predate this
 * page (e.g. logged as "transporte" from ExpensesTab for a perfectly valid
 * reason), so saving an edit here must never silently reclassify it. Rows
 * created here still show up in FxExpensesTab since it's the same table. */
export default function FxServiceCostsPage() {
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<ServiceCostForm>(emptyForm())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const fetchAll = async () => {
    try {
      const [expensesRes, suppliersRes] = await Promise.all([fxExpensesApi.list(), fxSuppliersApi.list()])
      setExpenses(expensesRes.data.expenses || [])
      setSuppliers(suppliersRes.data.suppliers || [])
    } catch (err) {
      console.error('Error fetching FX service costs:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const activeServiceSuppliers = useMemo(
    () => suppliers.filter((s) => s.is_active && s.supplier_type === 'servicio'),
    [suppliers]
  )

  const serviceCosts = useMemo(
    () => expenses.filter((e) => e.supplier_type === 'servicio'),
    [expenses]
  )

  const summary = useMemo(() => {
    // Only active (non-anulado) rows count toward spend totals, matching
    // how the rest of FX Admin's financial summaries treat is_active.
    const active = serviceCosts.filter((e) => e.is_active)
    const total = active.reduce((sum, e) => sum + Number(e.amount), 0)
    const byCategory = SERVICE_CATEGORY_ORDER.map((cat) => ({
      category: cat,
      total: active
        .filter((e) => (e.service_category || 'otro') === cat)
        .reduce((sum, e) => sum + Number(e.amount), 0),
    }))
    return { total, byCategory }
  }, [serviceCosts])

  const { sortedData, sortConfig, requestSort } = useSortableData(serviceCosts, {
    key: 'expense_date',
    direction: 'desc',
  })

  const exportColumns: ExportColumn<Expense>[] = [
    { key: 'expense_date', label: 'Fecha' },
    { key: 'supplier_name', label: 'Proveedor', format: (e) => e.supplier_name || '-' },
    { key: 'service_category', label: 'Tipo de Servicio', format: (e) => categoryBadgeLabel(e.service_category) },
    { key: 'description', label: 'Descripción' },
    { key: 'amount', label: 'Monto', format: (e) => Number(e.amount).toFixed(2) },
    { key: 'is_active', label: 'Estado', format: (e) => (e.is_active ? 'Activo' : 'Anulado') },
  ]

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const openNew = () => {
    setForm(emptyForm())
    setEditingId(null)
    setError('')
    setShowForm(true)
  }

  const openEdit = (exp: Expense) => {
    setForm({
      supplier_id: exp.supplier_id ? String(exp.supplier_id) : '',
      description: exp.description,
      amount: String(exp.amount),
      expense_date: exp.expense_date,
      payment_method: exp.payment_method || '',
      notes: exp.notes || '',
      // Preserve whatever category this row already had -- it may predate
      // this page (e.g. logged as 'transporte' from ExpensesTab) and must
      // not be silently reclassified just because it's being edited here.
      category: exp.category,
    })
    setEditingId(exp.id)
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.supplier_id) {
      setError('Selecciona un proveedor de servicio')
      return
    }
    if (!form.description.trim()) {
      setError('La descripción es requerida')
      return
    }
    const amountNum = Number(form.amount)
    if (!form.amount || Number.isNaN(amountNum) || amountNum <= 0) {
      setError('El monto debe ser un número mayor a 0')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = {
        category: form.category,
        description: form.description.trim(),
        amount: amountNum,
        expense_date: form.expense_date,
        payment_method: form.payment_method || null,
        supplier_id: Number(form.supplier_id),
        notes: form.notes.trim() || null,
      }
      if (editingId) {
        await fxExpensesApi.update(editingId, payload)
      } else {
        await fxExpensesApi.create(payload)
      }
      setShowForm(false)
      fetchAll()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar el costo de servicio')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (exp: Expense) => {
    try {
      await fxExpensesApi.update(exp.id, { is_active: !exp.is_active })
      fetchAll()
    } catch (err) {
      console.error('Error updating FX service cost:', err)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-espresso-800">Costos de Servicios — FX Admin</h1>
          <p className="text-sm text-espresso-500 mt-1 max-w-2xl">
            Gastos pagados a proveedores de servicio (marketing, logística/distribución, contabilidad). Se registran
            en la misma tabla de gastos generales -- también aparecen en Finanzas → Gastos.
          </p>
        </div>
        <button
          onClick={openNew}
          className="shrink-0 ml-4 px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium"
        >
          + Nuevo Costo de Servicio
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mb-6">
        <div className="bg-cream-50 shadow rounded-lg p-4">
          <div className="text-sm font-medium text-espresso-500">Total</div>
          <div className="mt-2 text-2xl font-bold text-espresso-800">S/ {summary.total.toFixed(2)}</div>
        </div>
        {summary.byCategory.map((row) => (
          <div key={row.category} className="bg-cream-50 shadow rounded-lg p-4">
            <div className="text-sm font-medium text-espresso-500">{SERVICE_CATEGORY_LABELS[row.category]}</div>
            <div className="mt-2 text-2xl font-bold text-espresso-800">S/ {row.total.toFixed(2)}</div>
          </div>
        ))}
      </div>

      {serviceCosts.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay costos de servicios registrados aún.</p>
        </div>
      ) : (
        <>
          <ExportBar data={serviceCosts} columns={exportColumns} filename="fx_costos_servicios" title="Costos de Servicios FX Admin" />
          <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-espresso-100">
                <thead className="bg-cream-50">
                  <tr>
                    <SortableTh label="Fecha" sortKey="expense_date" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Proveedor" sortKey="supplier_name" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Tipo de Servicio" sortKey="service_category" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Descripción" sortKey="description" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Monto" sortKey="amount" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Estado" sortKey="is_active" sortConfig={sortConfig} onSort={requestSort} />
                    <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-cream-50 divide-y divide-espresso-100">
                  {sortedData.map((exp) => (
                    <tr key={exp.id} className="hover:bg-cream-100">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{exp.expense_date}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{exp.supplier_name || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-cream-200 text-espresso-700">
                          {categoryBadgeLabel(exp.service_category)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-espresso-500 max-w-xs truncate" title={exp.description}>
                        {exp.description}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">
                        S/ {Number(exp.amount).toFixed(2)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${exp.is_active ? 'bg-green-100 text-green-800' : 'bg-cream-200 text-espresso-700'}`}>
                          {exp.is_active ? 'Activo' : 'Anulado'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end gap-1">
                          <TableActionButton onClick={() => openEdit(exp)} variant="primary">Editar</TableActionButton>
                          <TableActionButton onClick={() => handleToggleActive(exp)} variant="danger">
                            {exp.is_active ? 'Anular' : 'Reactivar'}
                          </TableActionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingId ? 'Editar Costo de Servicio' : 'Nuevo Costo de Servicio'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Proveedor *</label>
                <select name="supplier_id" value={form.supplier_id} onChange={handleChange} required
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
                  <option value="">Selecciona un proveedor</option>
                  {activeServiceSuppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.service_category ? ` — ${SERVICE_CATEGORY_LABELS[s.service_category]}` : ''}
                    </option>
                  ))}
                </select>
                {activeServiceSuppliers.length === 0 && (
                  <p className="mt-1 text-xs text-espresso-500">
                    No tienes proveedores de tipo "Servicio" activos -- agrega uno primero en la pestaña Proveedores.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Monto *</label>
                  <input type="number" step="0.01" min="0.01" name="amount" value={form.amount} onChange={handleChange} required
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Fecha *</label>
                  <input type="date" name="expense_date" value={form.expense_date} onChange={handleChange} required
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Descripción *</label>
                <input type="text" name="description" value={form.description} onChange={handleChange} required
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Método de pago</label>
                <select name="payment_method" value={form.payment_method} onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
                  <option value="">Sin especificar</option>
                  {PAYMENT_METHOD_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Notas</label>
                <textarea name="notes" value={form.notes} onChange={handleChange} rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50">
                  {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
